import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent, KEYS } from '../../../shared/index.js';
import { ToastType } from '../../portal/Toast/models/ToastType.js';
import { IToasterProps } from '../../portal/Toaster/models/index.js';
import Toaster from '../../portal/Toaster/Toaster.js';
import { Button } from '../Button/Button.js';

import { ICopyButtonConfiguration, ICopyButtonProps, ICopyButtonState } from './models/index.js';
import { COPY_URL_CATEGORY_KEY, COPY_URL_COMPONENT_KEY } from './CopyButton.meta.js';

const ACCEPT_KEYS = [KEYS.ENTER, KEYS.SPACE, KEYS.SPACEBAR];

export class CopyButton extends ComponentBase<ICopyButtonProps, ICopyButtonState, ICopyButtonConfiguration> {
	categoryKey = COPY_URL_CATEGORY_KEY;
	componentKey = COPY_URL_COMPONENT_KEY;

	toasterProps: IToasterProps = { items: [] };

	constructor(props: ICopyButtonProps) {
		super(props);

		makeObservable(this, {
			toasterProps: observable.ref,
			hideToaster: computed,
			successToastProps: computed,
			failureToastProps: computed,
		});
	}

	get hideToaster() {
		return this.props.toastProps && this.props.toastProps.hide;
	}

	get successToastProps() {
		return (this.props.toastProps && this.props.toastProps.successToast) || {};
	}

	get failureToastProps() {
		return (this.props.toastProps && this.props.toastProps.failureToast) || {};
	}

	handleClick = async (event: React.MouseEvent<HTMLButtonElement>, payload?: any) => {
		const { onClick } = this.props;
		if (payload) {
			try {
				event.stopPropagation();
				await navigator.clipboard.writeText(payload);
				if (onClick) {
					onClick(event, payload);
				}
				if (!this.hideToaster) {
					this.toasterProps = {
						items: [
							{
								toastId: common.commonUtils.generateGuid(),
								text: `Copied the following URL to clipboard: ${payload}`,
								type: ToastType.success,
								canClose: false,
								displaySeconds: 5,
								...this.successToastProps,
							},
						],
					};
				}
			} catch (e) {
				console.error(`Failed to copy ${payload} into user clipboard`);
				if (!this.hideToaster) {
					this.toasterProps = {
						items: [
							{
								toastId: common.commonUtils.generateGuid(),
								text: `Failed to copy the URL to clipboard`,
								type: ToastType.error,
								canClose: true,
								...this.failureToastProps,
							},
						],
					};
				}
			}
		} else {
			if (!this.hideToaster) {
				this.toasterProps = {
					items: [
						{
							toastId: common.commonUtils.generateGuid(),
							text: `Failed to copy the URL to clipboard`,
							type: ToastType.error,
							canClose: true,
							...this.failureToastProps,
						},
					],
				};
			}
		}
	};

	/**
	 * stop execution of parent event handlers but allow the button to execute the default behaviour of firing the click event on key press.
	 * This is needed in the scenario where the DocumentDownload has wrapping on click event to handle downloads when the label or download icon is clicked.
	 */
	handleOnKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
		if (ACCEPT_KEYS.indexOf(e.key as any) >= 0) {
			e.stopPropagation();
		}
	};

	render() {
		const { className, toastProps, ...buttonProps } = this.props;
		return (
			<>
				<Button
					className={className}
					{...buttonProps}
					onClick={this.handleClick}
					onKeyDown={this.handleOnKeyDown}
				/>
				<Toaster {...this.toasterProps} />
			</>
		);
	}
}

export default InjectedStyledComponent(observer(CopyButton), COPY_URL_CATEGORY_KEY, COPY_URL_COMPONENT_KEY);
